import React from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AppAppBar from "../../../components/AppAppBar";
import Footer from "../../../components/Footer";

const BRAND_YELLOW = "#ffca18";
const EXTENSION_URL =
    "https://chromewebstore.google.com/detail/marter/ldadolhckmdcpiodmjjgilbbmiakbhgp";

export default function Feature({
    title,
    subtitle,
    heroImage,
    heroAlt,
    date,
    quickAnswer,
    intro,
    benefits = [],
    sections = [],
    howItWorks = [],
    comparisonTable = null,
    faqs = [],
    relatedFeatures = [],
    relatedPosts = [],
    breadcrumb = { name: "Features", path: "/features" },
    ctaHeading = "Try Marter free on your next Walmart product",
    ctaBody = "One plan, $24/month, every feature included. Install the Chrome extension and open any Walmart product page.",
    meta = {}
}) {
    const location = useLocation();
    const baseUrl = "https://www.getmarter.com";
    const currentUrl = baseUrl + location.pathname;
    const canonical = String(meta?.canonical || currentUrl);

    const publishDate = date ? new Date(date) : new Date();
    const isoDate = publishDate.toISOString();
    const modifiedIso = meta?.dateModified
        ? new Date(meta.dateModified).toISOString()
        : isoDate;

    const rawSocialImage = meta?.ogImage || heroImage || "";
    // og:image must be an absolute URL — crawlers ignore relative paths
    const socialImage = rawSocialImage && !rawSocialImage.startsWith("http")
        ? `${baseUrl}${rawSocialImage}`
        : rawSocialImage;

    const softwareSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Marter",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, Chrome Extension, iOS, Android",
        description: String(meta?.description || ""),
        url: baseUrl,
        featureList: benefits.map((b) => b.title),
        offers: {
            "@type": "Offer",
            price: "24",
            priceCurrency: "USD",
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "24",
                priceCurrency: "USD",
                billingDuration: 1,
                billingIncrement: 1,
                unitCode: "MON"
            }
        },
        publisher: {
            "@type": "Organization",
            name: "Marter",
            logo: {
                "@type": "ImageObject",
                url: `${baseUrl}/fulllogot.png`
            }
        }
    };

    const faqSchema =
        faqs.length > 0
            ? {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: f.answer
                    }
                }))
            }
            : null;

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: baseUrl
            },
            {
                "@type": "ListItem",
                position: 2,
                name: breadcrumb.name,
                item: `${baseUrl}${breadcrumb.path}`
            },
            {
                "@type": "ListItem",
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };

    return (
        <>
            <Helmet>
                <title>{`${title} | Marter`}</title>
                <meta name="description" content={String(meta?.description || "")} />
                <meta name="keywords" content={String(meta?.keywords || "")} />
                <link rel="canonical" href={canonical} />

                <meta property="og:title" content={title} />
                <meta property="og:description" content={String(meta?.description || "")} />
                {socialImage && <meta property="og:image" content={socialImage} />}
                <meta property="og:url" content={canonical} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Marter - Walmart Seller Tool" />
                <meta property="article:published_time" content={isoDate} />
                <meta property="article:modified_time" content={modifiedIso} />

                <meta name="twitter:card" content={socialImage ? "summary_large_image" : "summary"} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={String(meta?.description || "")} />
                {socialImage && <meta name="twitter:image" content={socialImage} />}

                <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
                {faqSchema && (
                    <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                )}
            </Helmet>

            <AppAppBar />

            <Box sx={{ bgcolor: "#ffffff", pt: { xs: "84px", md: "92px" } }}>
                <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
                    {/* Hero */}
                    <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center" sx={{ mb: { xs: 5, md: 7 } }}>
                        <Grid item xs={12} md={heroImage ? 6 : 12}>
                            <Chip
                                label="Marter Feature"
                                size="small"
                                sx={{ bgcolor: BRAND_YELLOW, fontWeight: 700, mb: 2 }}
                            />
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 800,
                                    color: "#1a1a1a",
                                    fontSize: { xs: "2rem", md: "2.75rem" },
                                    lineHeight: 1.2,
                                    mb: 2
                                }}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant="h2"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: { xs: "1.05rem", md: "1.2rem" },
                                    lineHeight: 1.65,
                                    mb: 3
                                }}
                            >
                                {subtitle}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <Button
                                    variant="contained"
                                    href={EXTENSION_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        bgcolor: BRAND_YELLOW,
                                        color: "#1a1a1a",
                                        fontWeight: 700,
                                        px: 3,
                                        py: 1.25,
                                        "&:hover": { bgcolor: "#e6b615" }
                                    }}
                                >
                                    Add Marter to Chrome — Free
                                </Button>
                                <Button
                                    variant="outlined"
                                    component={RouterLink}
                                    to="/pricing"
                                    sx={{
                                        borderColor: "#1a1a1a",
                                        color: "#1a1a1a",
                                        fontWeight: 700,
                                        px: 3,
                                        py: 1.25
                                    }}
                                >
                                    See pricing — $24/mo
                                </Button>
                            </Stack>
                        </Grid>
                        {heroImage && (
                            <Grid item xs={12} md={6}>
                                <Box
                                    component="img"
                                    src={heroImage}
                                    alt={heroAlt || title}
                                    sx={{
                                        width: "100%",
                                        borderRadius: 2,
                                        border: "1px solid #eaeaea"
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>

                    {/* Quick answer — the passage AI engines and featured snippets lift */}
                    {quickAnswer && (
                        <Box
                            sx={{
                                bgcolor: "#fffbea",
                                border: `2px solid ${BRAND_YELLOW}`,
                                borderRadius: 2,
                                p: 3,
                                mb: 5
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{ fontWeight: 700, color: "#b8860b", letterSpacing: 1.5 }}
                            >
                                Quick Answer
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {quickAnswer}
                            </Typography>
                        </Box>
                    )}

                    {intro && (
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 5 }}>
                            {intro}
                        </Typography>
                    )}

                    {/* Benefits */}
                    {benefits.length > 0 && (
                        <Box sx={{ mb: 6 }}>
                            <Grid container spacing={3}>
                                {benefits.map((b, i) => (
                                    <Grid item xs={12} sm={6} md={4} key={i}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                borderRadius: 2,
                                                boxShadow: "none",
                                                border: "1px solid #eaeaea"
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <CheckCircleIcon sx={{ color: BRAND_YELLOW, mb: 1 }} />
                                                <Typography variant="h3" sx={{ fontSize: "1.1rem", fontWeight: 700, mb: 1 }}>
                                                    {b.title}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                                                    {b.body}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Body sections */}
                    {sections.map((s, i) => (
                        <Box key={i} id={s.id} sx={{ mb: 5, scrollMarginTop: "100px" }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: "#1a1a1a",
                                    fontSize: { xs: "1.5rem", md: "1.9rem" },
                                    mb: 2
                                }}
                            >
                                {s.title}
                            </Typography>
                            <Box sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{s.content}</Box>
                        </Box>
                    ))}

                    {/* How it works */}
                    {howItWorks.length > 0 && (
                        <Box sx={{ mb: 6 }}>
                            <Typography
                                variant="h2"
                                sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", md: "1.9rem" }, mb: 3 }}
                            >
                                How it works
                            </Typography>
                            <Stack spacing={2}>
                                {howItWorks.map((step, i) => (
                                    <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                minWidth: 36,
                                                height: 36,
                                                borderRadius: "50%",
                                                bgcolor: BRAND_YELLOW,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 800
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}>
                                            {step}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Comparison table */}
                    {comparisonTable && (
                        <Box sx={{ mb: 6 }}>
                            <Typography
                                variant="h2"
                                sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", md: "1.9rem" }, mb: 2 }}
                            >
                                {comparisonTable.title}
                            </Typography>
                            <TableContainer sx={{ border: "1px solid #eaeaea", borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#fafafa" }}>
                                            {comparisonTable.columns.map((c, i) => (
                                                <TableCell key={i} sx={{ fontWeight: 700 }}>
                                                    {c}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {comparisonTable.rows.map((row, i) => (
                                            <TableRow key={i}>
                                                {row.map((cell, j) => (
                                                    <TableCell key={j} sx={{ fontWeight: j === 0 ? 600 : 400 }}>
                                                        {cell}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* FAQs */}
                    {faqs.length > 0 && (
                        <Box id="faq" sx={{ mb: 6, scrollMarginTop: "100px" }}>
                            <Typography
                                variant="h2"
                                sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", md: "1.9rem" }, mb: 3 }}
                            >
                                Frequently Asked Questions
                            </Typography>
                            {faqs.map((f, i) => (
                                <Box key={i} sx={{ mb: 3 }}>
                                    <Typography variant="h3" sx={{ fontSize: "1.1rem", fontWeight: 700, mb: 1 }}>
                                        {f.question}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}>
                                        {f.answerNode || f.answer}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* CTA */}
                    <Box
                        sx={{
                            bgcolor: "#f8f9fa",
                            borderRadius: 2,
                            p: { xs: 3, md: 5 },
                            textAlign: "center",
                            mb: 6
                        }}
                    >
                        <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: "1.4rem", md: "1.75rem" }, mb: 1.5 }}>
                            {ctaHeading}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "text.secondary", fontSize: "1.05rem", mb: 3 }}>
                            {ctaBody}
                        </Typography>
                        <Button
                            variant="contained"
                            href={EXTENSION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                bgcolor: BRAND_YELLOW,
                                color: "#1a1a1a",
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                "&:hover": { bgcolor: "#e6b615" }
                            }}
                        >
                            Get Marter Free
                        </Button>
                    </Box>

                    {/* Internal links */}
                    {(relatedFeatures.length > 0 || relatedPosts.length > 0) && (
                        <>
                            <Divider sx={{ mb: 4 }} />
                            <Grid container spacing={4}>
                                {relatedFeatures.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: "1.15rem", mb: 2 }}>
                                            More Marter features
                                        </Typography>
                                        <Stack spacing={1}>
                                            {relatedFeatures.map((r, i) => (
                                                <RouterLink
                                                    key={i}
                                                    to={r.url}
                                                    style={{ color: "#1a1a1a", fontWeight: 600 }}
                                                >
                                                    {r.title}
                                                </RouterLink>
                                            ))}
                                        </Stack>
                                    </Grid>
                                )}
                                {relatedPosts.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: "1.15rem", mb: 2 }}>
                                            Related reading
                                        </Typography>
                                        <Stack spacing={1}>
                                            {relatedPosts.map((r, i) => (
                                                <RouterLink
                                                    key={i}
                                                    to={r.url}
                                                    style={{ color: "#1a1a1a", fontWeight: 600 }}
                                                >
                                                    {r.title}
                                                </RouterLink>
                                            ))}
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                </Container>
                <Footer />
            </Box>
        </>
    );
}
